import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Send, Sparkles, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = 'beta_session_id';

const STARTER_SUGGESTIONS = [
  'Quanto costa un sito?',
  'Cosa posso vendere con un e-commerce?',
  'Come funziona il booking?',
  'Cos\u2019\u00e8 il men\u00f9 3D?',
];

function getOrCreateSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `beta-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (e) {
    return `beta-${Date.now()}`;
  }
}

/**
 * BetaRobot — animated friendly robot avatar.
 *  - antenna with bouncing light
 *  - blinking eyes
 *  - subtle body bobbing
 *  - optional waving hand (set wave=true)
 */
function BetaRobot({ size = 44, wave = true }) {
  return (
    <div
      className="relative beta-bob"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 64 64" className="w-full h-full overflow-visible">
        {/* antenna */}
        <line x1="32" y1="6" x2="32" y2="14" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="32" cy="5" r="2.6" fill="#fbbf24" className="beta-blink-dot" />

        {/* head */}
        <rect
          x="14"
          y="14"
          width="36"
          height="28"
          rx="9"
          fill="#ffffff"
          stroke="#bfdbfe"
          strokeWidth="1.5"
        />
        {/* visor band */}
        <rect x="18" y="22" width="28" height="12" rx="6" fill="#0f1620" />

        {/* eyes */}
        <g className="beta-eyes">
          <circle cx="26" cy="28" r="2.4" fill="#5eb0e8" />
          <circle cx="38" cy="28" r="2.4" fill="#5eb0e8" />
        </g>

        {/* cheek lights */}
        <circle cx="19" cy="38" r="1.2" fill="#5eb0e8" opacity="0.85" />
        <circle cx="45" cy="38" r="1.2" fill="#5eb0e8" opacity="0.85" />

        {/* body */}
        <rect x="20" y="44" width="24" height="16" rx="5" fill="#ffffff" stroke="#bfdbfe" strokeWidth="1.5" />
        <circle cx="32" cy="52" r="2" fill="#1e6cb0" />

        {/* left arm (static) */}
        <rect x="12" y="46" width="3.5" height="10" rx="1.8" fill="#ffffff" stroke="#bfdbfe" strokeWidth="1" />

        {/* right arm — WAVING */}
        <g
          className={wave ? 'beta-wave' : ''}
          style={{ transformOrigin: '48px 48px' }}
        >
          <rect x="46" y="40" width="3.5" height="10" rx="1.8" fill="#ffffff" stroke="#bfdbfe" strokeWidth="1" />
          <circle cx="47.8" cy="38" r="3" fill="#ffffff" stroke="#bfdbfe" strokeWidth="1" />
        </g>
      </svg>
    </div>
  );
}

function BetaMini({ size = 28 }) {
  return <BetaRobot size={size} wave={false} />;
}

export default function BetaBot() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(getOrCreateSessionId);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text:
        "Ciao! Sono BETA, l'assistente di B-WEB. \uD83D\uDC4B Posso aiutarti con info sui pacchetti, prezzi, processi e i nostri servizi. Cosa vuoi sapere?",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  const send = async (text) => {
    const t = (text || input).trim();
    if (!t || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: t }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: t,
      }, { timeout: 45000 });
      const reply = res?.data?.reply ||
        "Mi spiace, non ho capito. Scrivimi su WhatsApp al +39 338 346 3712.";
      setMessages((m) => [...m, { role: 'assistant', text: reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text:
            "Sto avendo un problema tecnico. Scrivimi direttamente su WhatsApp al +39 338 346 3712, ti rispondo io di persona.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send();
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Apri chat con BETA"
          className="fixed z-[55] bottom-5 left-5 md:bottom-6 md:left-6 group"
        >
          {/* ping ring */}
          <span className="absolute inset-0 rounded-full bg-[#1e6cb0]/35 animate-ping" />

          {/* speech bubble label */}
          <span className="absolute -top-9 left-12 hidden sm:block px-3 py-1.5 rounded-full bg-white text-[#1e6cb0] text-[12px] font-semibold tracking-tight shadow-[0_10px_24px_-10px_rgba(30,108,176,0.45)] border border-[#1e6cb0]/15 beta-bubble whitespace-nowrap">
            <span className="beta-text-shimmer">Ciao! Sono BETA</span>
            <span
              className="absolute -bottom-1.5 left-4 w-3 h-3 rotate-45 bg-white border-l border-b border-[#1e6cb0]/15"
            />
          </span>

          <span className="relative flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-full bg-gradient-to-br from-[#1e6cb0] to-[#1857a0] text-white shadow-[0_18px_40px_-12px_rgba(30,108,176,0.6)] hover:shadow-[0_22px_50px_-12px_rgba(30,108,176,0.75)] transition-shadow">
            <BetaRobot size={40} wave />
            <span className="text-[14px] font-semibold pr-1 beta-text-shimmer">Chiedi a BETA</span>
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-[60] bottom-0 left-0 right-0 md:right-auto md:bottom-6 md:left-6 md:w-[380px] flex justify-start pointer-events-none"
        >
          <div className="pointer-events-auto w-full md:w-[380px] h-[calc(100vh-1rem)] md:h-[600px] md:max-h-[80vh] flex flex-col bg-white md:rounded-3xl border border-black/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)] overflow-hidden beta-panel-in">
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#1e6cb0] to-[#1857a0] text-white px-5 py-4 flex items-center gap-3">
              <BetaRobot size={46} wave />
              <div className="flex-1 leading-tight">
                <div className="flex items-center gap-1.5">
                  <span className="font-display text-[17px] font-extrabold tracking-tight beta-text-shimmer">BETA</span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/15 text-[9px] font-bold tracking-[0.16em] uppercase">
                    <Sparkles className="w-2.5 h-2.5" /> AI
                  </span>
                </div>
                <div className="text-[11.5px] text-white/75">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22c55e] mr-1.5 align-middle beta-blink-dot" />
                  Assistente B-WEB &middot; Online
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Chiudi"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3 bg-[#f6f4ee]">
              {messages.map((m, i) => (
                <Message key={i} role={m.role} text={m.text} />
              ))}
              {loading && (
                <div className="flex items-end gap-2 beta-msg-in">
                  <BetaMini size={28} />
                  <div className="px-3.5 py-2.5 rounded-2xl rounded-bl-md bg-white border border-black/5 text-[14px] text-neutral-500 inline-flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="beta-typing">sto pensando</span>
                  </div>
                </div>
              )}

              {messages.length <= 1 && !loading && (
                <div className="pt-2 beta-msg-in" style={{ animationDelay: '300ms' }}>
                  <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-neutral-500 mb-2">
                    Domande veloci
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {STARTER_SUGGESTIONS.map((s, idx) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="text-[12.5px] px-3 py-1.5 rounded-full bg-white border border-black/10 text-neutral-700 hover:border-[#1e6cb0] hover:text-[#1e6cb0] transition-colors beta-msg-in"
                        style={{ animationDelay: `${400 + idx * 80}ms` }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={handleSubmit}
              className="border-t border-black/10 bg-white px-3 py-3 flex items-end gap-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi a BETA..."
                disabled={loading}
                className="flex-1 px-4 py-3 text-[14.5px] rounded-full bg-neutral-100 border border-transparent focus:border-[#1e6cb0] focus:bg-white outline-none transition-colors disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="w-11 h-11 rounded-full bg-[#1e6cb0] hover:bg-[#1857a0] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                aria-label="Invia"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
            <div className="px-4 pb-3 text-[10.5px] text-neutral-500 bg-white text-center">
              BETA pu&ograve; sbagliare. Per cose precise scrivi su{' '}
              <a
                href="https://wa.me/393383463712"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1e6cb0] font-semibold hover:underline"
              >
                WhatsApp
              </a>
              .
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Message({ role, text }) {
  const isUser = role === 'user';

  // Auto-link URLs in assistant text
  const renderText = (t) => {
    const parts = t.split(/(https?:\/\/\S+|wa\.me\/\d+|\+?\d{2,3}[\s.-]?\d{2,4}[\s.-]?\d{3,4}[\s.-]?\d{3,4})/g);
    return parts.map((p, i) => {
      if (!p) return null;
      if (/^https?:\/\//.test(p) || /^wa\.me\//.test(p)) {
        const href = p.startsWith('http') ? p : `https://${p}`;
        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-semibold break-all"
          >
            {p}
          </a>
        );
      }
      if (/^\+?\d{2,3}[\s.-]?\d{2,4}/.test(p) && p.replace(/\D/g, '').length >= 8) {
        return (
          <a key={i} href={`https://wa.me/${p.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="underline font-semibold">
            {p}
          </a>
        );
      }
      return <span key={i}>{p}</span>;
    });
  };

  return (
    <div className={`flex items-end gap-2 beta-msg-in ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && <BetaMini size={28} />}
      <div
        className={`max-w-[78%] px-3.5 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap break-words ${
          isUser
            ? 'bg-[#1e6cb0] text-white rounded-2xl rounded-br-md'
            : 'bg-white text-neutral-800 border border-black/5 rounded-2xl rounded-bl-md'
        }`}
      >
        {renderText(text)}
      </div>
    </div>
  );
}
